import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
  Divider,
  Alert,
} from "@mui/material";
import { toast } from "material-react-toastify";
import { handleSpringDataMutationRequest } from "../../api";
import { razorPayKey } from "../CalendarPage/constants/AmplitudeConstants";
import { channelLogoMap } from "../CalendarPage/constants/Calendar-constants";

/**
 * Account-level Pay Now for total outstanding (net after TDS).
 * One credit clears all dues via bulk capture.
 * Canonical backend path: capture-account-dues-payment (bulk account-dues;
 * ledger_id not sent). Deprecated alias: capture-invoice-payment.
 */
const AccountPayNowModal = ({
  open,
  onClose,
  groupId,
  accountInfo,
  currSymbol,
  grossOutstanding,
  onPaymentSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorShow, setErrorShow] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [razorpayFinalKey] = useState(razorPayKey);
  const [imageUrl] = useState(channelLogoMap.STAYFLEXI_OM);

  const grossAmount = useMemo(() => {
    const raw = Number(grossOutstanding);
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    return Math.abs(raw);
  }, [grossOutstanding]);

  // TDS % from existing account get-single payload (ACCOUNTS.tds_percentage).
  // Do NOT call nonexistent get-tds-config.
  const tdsPercentage = useMemo(() => {
    const fromDetails = accountInfo?.account_details?.tds_percentage;
    if (fromDetails !== undefined && fromDetails !== null && fromDetails !== "") {
      const n = Number(fromDetails);
      return Number.isFinite(n) ? n : 0;
    }
    const fromRoot = accountInfo?.tds_percentage;
    if (fromRoot !== undefined && fromRoot !== null && fromRoot !== "") {
      const n = Number(fromRoot);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  }, [accountInfo]);

  const tdsAmount = (grossAmount * tdsPercentage) / 100;
  const netPayable = Math.max(0, grossAmount - tdsAmount);

  const buildCapturePayload = (razorpayPaymentId) => ({
    group_id: groupId,
    hotel_id: groupId,
    "razorpay-pg-id": razorpayPaymentId,
    razorpay_payment_id: razorpayPaymentId,
    amount: netPayable,
    currency: "INR",
    gross_amount: grossAmount,
    tds_percentage: tdsPercentage,
    tds_amount: tdsAmount,
    net_payable: netPayable,
    net_amount: netPayable,
    // No ledger_id — account-level bulk clear of all dues.
  });

  const razorpayOptions = {
    key: razorpayFinalKey,
    amount: Math.round(netPayable * 100),
    currency: "INR",
    name: "Stayflexi",
    description: "Pay outstanding account dues",
    image: imageUrl,
    handler: function (response) {
      setLoading(true);
      const payload = buildCapturePayload(response.razorpay_payment_id);

      // Canonical bulk account-dues path (ledger_id not sent).
      handleSpringDataMutationRequest(
        "POST",
        `core/api/v1/payments/razorpay/capture-account-dues-payment/?groupId=${groupId}&hotelId=${groupId}`,
        payload
      )
        .then((resp) => {
          toast.success(
            resp?.message || resp || "Payment successful! Account dues cleared."
          );
          setLoading(false);
          onPaymentSuccess && onPaymentSuccess();
          onClose();
        })
        .catch((err) => {
          console.error("Account dues payment capture error:", err);
          toast.error(
            err?.response?.data?.message ||
              err?.message ||
              "Payment capture failed. Please contact support."
          );
          setLoading(false);
        });
    },
    prefill: {
      name: accountInfo?.account_details?.account_name || "",
      email: accountInfo?.account_details?.account_billing_email || "",
      contact: accountInfo?.account_details?.account_billing_phone || "",
    },
    notes: {
      account_id: groupId,
      module_source: "ACCOUNT_DUES_PAYMENT",
      gross_amount: String(grossAmount),
      tds_percentage: String(tdsPercentage),
      net_payable: String(netPayable),
    },
    theme: {
      color: "#1ab394",
      hide_topbar: false,
    },
  };

  const handlePayNow = () => {
    if (netPayable <= 0) {
      toast.error("Invalid payment amount");
      return;
    }

    const rzp = new window.Razorpay(razorpayOptions);
    rzp.open();
    rzp.on("payment.failed", function (response) {
      setErrorShow(true);
      setErrorMessage(response?.error?.description || "Payment failed");
      setTimeout(() => {
        setErrorShow(false);
      }, 5000);
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="account-pay-now-modal"
      aria-describedby="account-pay-now-description"
    >
      <Box className="settleDuesModal">
        <div className="settleDuesWrapper">
          <div className="settleDuesHeader">
            <div className="settleDuesHeading">Pay outstanding dues</div>
            <div className="close" onClick={onClose}>
              X
            </div>
          </div>

          <div className="amountWrapper">
            {errorShow && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Payment bifurcation
            </Typography>

            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography>Gross outstanding:</Typography>
                <Typography fontWeight={500}>
                  {currSymbol} {parseFloat(grossAmount).toFixed(2)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography color="text.secondary">
                  Account TDS ({tdsPercentage}%):
                </Typography>
                <Typography
                  color={tdsPercentage > 0 ? "error.main" : "text.secondary"}
                  fontWeight={500}
                >
                  {tdsPercentage > 0 ? "-" : ""} {currSymbol}{" "}
                  {parseFloat(tdsAmount).toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Net payable:
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary.main">
                  {currSymbol} {parseFloat(netPayable).toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {tdsPercentage > 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                TDS of {tdsPercentage}% is applied at account level. One payment
                clears all outstanding dues. You will be charged the net payable
                of {currSymbol} {parseFloat(netPayable).toFixed(2)}.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                One payment clears all outstanding dues for this account.
              </Alert>
            )}

            <div className="settleDuesSubmitBtns">
              <Button className="cancelBtn" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                className="submit"
                variant="custom-button"
                onClick={handlePayNow}
                disabled={loading || netPayable <= 0}
              >
                {loading ? (
                  <CircularProgress size={15} color="inherit" sx={{ ml: 1.5 }} />
                ) : (
                  `Pay ${currSymbol} ${parseFloat(netPayable).toFixed(2)}`
                )}
              </Button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default AccountPayNowModal;
